import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, Eye, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ProposalTemplatesProps {
  open: boolean;
  onClose: () => void;
}

const ProposalTemplates: React.FC<ProposalTemplatesProps> = ({ open, onClose }) => {
  const { toast } = useToast();

  const templates = {
    advertising: [
      {
        id: 1,
        name: "Display Advertising Proposal",
        description: "Comprehensive proposal for banner and display advertising campaigns",
        category: "Display",
        pages: 8,
        includes: ["Campaign Overview", "Target Audience", "Pricing", "Metrics", "Timeline"]
      },
      {
        id: 2,
        name: "Social Media Campaign",
        description: "Complete social media advertising and sponsorship proposal",
        category: "Social",
        pages: 6,
        includes: ["Platform Strategy", "Content Plan", "Engagement Metrics", "ROI Projections"]
      },
      {
        id: 3,
        name: "Newsletter Sponsorship",
        description: "Email marketing and newsletter sponsorship proposal template",
        category: "Email",
        pages: 4,
        includes: ["Audience Demographics", "Placement Options", "Performance Data"]
      }
    ],
    partnerships: [
      {
        id: 4,
        name: "Strategic Partnership Proposal",
        description: "Long-term partnership and collaboration agreement template",
        category: "Partnership",
        pages: 12,
        includes: ["Partnership Goals", "Mutual Benefits", "Revenue Sharing", "Terms", "Next Steps"]
      },
      {
        id: 5,
        name: "Technology Integration",
        description: "Technical partnership and API integration proposal",
        category: "Technology",
        pages: 10,
        includes: ["Technical Specs", "Integration Timeline", "Support Structure", "Pricing"]
      }
    ],
    affiliate: [
      {
        id: 6,
        name: "Affiliate Program Proposal",
        description: "Commission-based affiliate marketing partnership proposal",
        category: "Affiliate",
        pages: 6,
        includes: ["Commission Structure", "Marketing Materials", "Tracking", "Payment Terms"]
      },
      {
        id: 7,
        name: "Referral Partnership",
        description: "Referral-based partnership program proposal template",
        category: "Referral",
        pages: 5,
        includes: ["Referral Process", "Incentives", "Tracking System", "Success Metrics"]
      }
    ]
  };

  const handlePreview = (templateId: number) => {
    toast({
      title: "Template Preview",
      description: "Template preview functionality coming soon!",
    });
  };

  const handleDownload = (templateId: number, templateName: string) => {
    toast({
      title: "Download Started",
      description: `Downloading ${templateName} template...`,
    });
    // In a real implementation, this would trigger a file download
  };

  const handleCopy = (templateId: number, templateName: string) => {
    toast({
      title: "Template Copied",
      description: `${templateName} template copied to your workspace.`,
    });
  };

  const TemplateCard = ({ template }: { template: any }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{template.name}</CardTitle>
            <CardDescription className="mt-1">{template.description}</CardDescription>
          </div>
          <Badge variant="outline">{template.category}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="h-4 w-4" />
            <span>{template.pages} pages</span>
          </div>
          
          <div>
            <p className="text-sm font-medium mb-2">Includes:</p>
            <div className="flex flex-wrap gap-1">
              {template.includes.map((item: string, index: number) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {item}
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePreview(template.id)}
              className="flex-1"
            >
              <Eye className="h-3 w-3 mr-1" />
              Preview
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleDownload(template.id, template.name)}
              className="flex-1"
            >
              <Download className="h-3 w-3 mr-1" />
              Download
            </Button>
            <Button
              size="sm"
              onClick={() => handleCopy(template.id, template.name)}
              className="flex-1"
            >
              <Copy className="h-3 w-3 mr-1" />
              Use
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Proposal Templates
          </DialogTitle>
          <DialogDescription>
            Professional proposal templates to accelerate your sales process
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="advertising" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="advertising">Advertising</TabsTrigger>
            <TabsTrigger value="partnerships">Partnerships</TabsTrigger>
            <TabsTrigger value="affiliate">Affiliate</TabsTrigger>
          </TabsList>

          <TabsContent value="advertising" className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Advertising Campaign Templates</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Ready-to-use templates for advertising sales and campaign proposals
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.advertising.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="partnerships" className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Partnership Templates</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Comprehensive templates for strategic partnerships and collaborations
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.partnerships.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="affiliate" className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Affiliate Program Templates</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Templates for affiliate marketing and referral partnership proposals
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.affiliate.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between items-center mt-6 pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            All templates are customizable and include example content
          </div>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProposalTemplates;